<template>
  <main class="booking-success">

    <!-- code 完全不存在：不讓頁面空白 -->
    <div v-if="!code" class="bs-error">
      <p class="bs-error__title">無法取得預約資訊</p>
      <p class="bs-error__msg">
        預約代碼遺失，可能是頁面被直接開啟或重新整理後資料已清除。
      </p>
      <div class="bs-actions">
        <router-link :to="{ name: 'BookingHome' }" class="bs-btn bs-btn--secondary">
          前往預約頁面
        </router-link>
        <router-link :to="{ name: 'BookingQuery' }" class="bs-btn bs-btn--primary">
          查詢已有預約
        </router-link>
      </div>
    </div>

    <!-- 正常成功畫面 -->
    <div v-else class="bs-card">
      <!-- 成功圖示 -->
      <div class="bs-icon">
        <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="26" cy="26" r="25" stroke="#8b6f5c" stroke-width="2"/>
          <path d="M14 27L22 35L38 19" stroke="#8b6f5c" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <h1 class="bs-title">
        <template v-if="storeLineId">請傳送預約訊息給店家</template>
        <template v-else-if="viaLine">您已透過 LINE 完成預約</template>
        <template v-else>預約申請已送出</template>
      </h1>
      <p class="bs-subtitle">
        <template v-if="storeLineId">傳送後，等待店家確認即完成預約</template>
        <template v-else-if="viaLine">店家確認後將通知您</template>
        <template v-else>確認信已寄至您的信箱，請前往信箱完成驗證</template>
      </p>

      <!-- LINE 傳訊息提示 + 按鈕（有 store_line_id 就顯示，email/LINE 都適用）-->
      <div v-if="storeLineId" class="bs-line-notice">
        <p class="bs-line-notice__title">⚠️ 請務必完成以下步驟</p>
        <p class="bs-line-notice__desc"><strong>店家確認後預約才算成立</strong></p>
        <!-- 預覽訊息內容 -->
        <div class="bs-line-msg-box">
          <p class="bs-line-msg-box__text">{{ prefillMsg }}</p>
        </div>
      </div>
      <!-- 複製訊息 + 開啟 LINE（分開兩個按鈕）-->
      <div v-if="storeLineId" class="bs-line-btns">
        <button type="button" class="bs-btn bs-btn--copy" @click="copyMsg">
          {{ copied ? '✅ 已複製訊息' : '📋 複製預約訊息' }}
        </button>
        <button type="button" class="bs-btn bs-btn--line" @click="openLine">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width:20px;height:20px;flex-shrink:0">
            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.070 9.437-6.977C23.176 14.393 24 12.458 24 10.314"/>
          </svg>
          開啟 LINE 傳送
        </button>
      </div>

      <!-- 店家未設定 LINE：提示改用電話聯絡 -->
      <p v-if="viaLine && !storeLineId" class="bs-no-line-hint">
        預約申請已送出，店家將盡快與您確認，請保持電話暢通
      </p>

      <!-- 信箱提示（非 LINE 預約才顯示；有 LINE 區塊時改為次要樣式）-->
      <div v-if="!viaLine" class="bs-code-box" :class="{ 'bs-code-box--secondary': storeLineId }">
        <p class="bs-code-box__label">確認信寄送至</p>
        <p class="bs-code-box__email">{{ displayEmail }}</p>
        <p class="bs-code-box__hint">若未收到，請檢查垃圾郵件資料夾</p>
      </div>

      <!-- 提醒文字 -->
      <ul class="bs-reminders">
        <li>如需取消，請於預約時間 <strong>48 小時前</strong>操作</li>
        <li>線上預約截止為預約時間前 <strong>2 小時</strong></li>
        <li>急需異動請直接聯絡店家</li>
      </ul>

      <!-- 狀態說明（有 LINE 互動時才顯示）-->
      <div v-if="!isMember && storeLineId" class="bs-status-legend">
        <p class="bs-status-legend__title">預約狀態說明</p>
        <p>🕐 <strong>待確認</strong> — 請主動傳送預約訊息，通知店家確認</p>
        <p>✅ <strong>已確認</strong> — 店家已確認，預約成立</p>
      </div>

      <!-- CTA：依登入狀態分流 -->
      <div class="bs-actions">

        <!-- 已登入會員 -->
        <template v-if="isMember">
          <router-link
            :to="{ name: 'MemberReservations' }"
            class="bs-btn bs-btn--primary"
          >
            查看我的預約
          </router-link>
          <router-link :to="{ name: 'BookingHome' }" class="bs-btn bs-btn--secondary">
            繼續找店家
          </router-link>
        </template>

        <!-- 訪客 -->
        <template v-else>
          <!-- LINE 預約 → 用 LINE 查詢 -->
          <router-link
            v-if="viaLine"
            :to="{ name: 'BookingQuery', query: { via: 'line' } }"
            class="bs-btn bs-btn--line"
          >
            用 LINE 查詢預約
          </router-link>
          <!-- 信箱預約 → 用信箱查詢 -->
          <router-link
            v-else
            :to="{ name: 'BookingQuery', query: { email: bookingStore.reservationEmail } }"
            class="bs-btn bs-btn--primary"
          >
            用信箱查詢預約
          </router-link>
          <router-link :to="{ name: 'BookingHome' }" class="bs-btn bs-btn--secondary">
            繼續找店家
          </router-link>
        </template>

        <!-- 返回網站首頁（共用）-->
        <router-link :to="{ name: 'home' }" class="bs-btn bs-btn--ghost">
          返回首頁
        </router-link>
      </div>
    </div>

  </main>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useBookingStore } from '@/store/bookingStore'

const route        = useRoute()
const bookingStore = useBookingStore()

const code = computed(() =>
  bookingStore.reservationCode || route.query.code || ''
)

const displayEmail = computed(() =>
  bookingStore.reservationEmail || route.query.email || '您填寫的信箱'
)

const isMember = computed(() => {
  if (bookingStore.reservationCode) return bookingStore.isMember
  return route.query.is_member === '1'
})

// LINE 相關
const viaLine     = computed(() => route.query.via_line === '1')
const storeLineId = computed(() => route.query.store_line_id || '')
const slotDate    = computed(() => route.query.slot_date    || '')
const slotTime    = computed(() => route.query.slot_time    || '')
const slotEndTime = computed(() => route.query.slot_end_time || '')
const serviceName = computed(() => route.query.service_name  || '')
const prefillMsg  = computed(() => {
  const lines = ['您好，我剛完成預約，請協助確認！']
  if (serviceName.value)
    lines.push(`課程：${serviceName.value}`)
  if (slotDate.value || slotTime.value) {
    const timeRange = slotEndTime.value
      ? `${slotTime.value} ~ ${slotEndTime.value}`
      : slotTime.value
    lines.push(`時間：${slotDate.value} ${timeRange}`.trim())
  }
  lines.push('謝謝！')
  return lines.join('\n')
})
const copied = ref(false)
function copyMsg() {
  navigator.clipboard.writeText(prefillMsg.value).then(() => {
    copied.value = true
    setTimeout(() => { copied.value = false }, 4000)
  }).catch(() => {})
}
function openLine() {
  // 同視窗跳轉，讓 iOS/Android 系統直接攔截開啟 LINE，比 _blank 穩定
  window.location.href = lineContactUrl.value
}
const lineContactUrl = computed(() => {
  if (!storeLineId.value) return '#'
  const id = storeLineId.value.trim()
  // 官方帳號統一加 @ 前綴，使用 ti/p 格式穩定開啟 LINE OA 頁面
  const withAt = id.startsWith('@') ? id : '@' + id
  return `https://line.me/ti/p/${encodeURIComponent(withAt)}`
})
</script>

<style scoped>
.booking-success {
  max-width: 520px;
  margin: 0 auto;
  padding: 40px 16px 80px;
  text-align: center;
}

/* code 遺失的錯誤畫面 */
.bs-error {
  padding: 48px 0;
}
.bs-error__title {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 12px;
}
.bs-error__msg {
  color: #888;
  font-size: 0.95rem;
  margin-bottom: 32px;
  line-height: 1.6;
}

/* 成功卡片 */
.bs-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

/* 勾勾圖示 */
.bs-icon {
  width: 72px;
  height: 72px;
  margin-bottom: 20px;
}
.bs-icon svg {
  width: 100%;
  height: 100%;
}

/* 標題 */
.bs-title {
  font-size: 1.8rem;
  color: #333;
  margin: 0 0 8px;
}
.bs-subtitle {
  font-size: 0.95rem;
  color: #888;
  margin: 0 0 28px;
}

/* 代碼區塊 */
.bs-code-box {
  background: #f9f6f3;
  border: 1px solid #e8ddd7;
  border-radius: 12px;
  padding: 20px 24px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 24px;
}
.bs-code-box__label {
  font-size: 0.78rem;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 8px;
}
.bs-code-box__email {
  font-size: 1.15rem;
  font-weight: 600;
  color: #8b6f5c;
  margin: 0 0 8px;
  word-break: break-all;
}
.bs-code-box__hint {
  font-size: 0.82rem;
  color: #bbb;
  margin: 0;
}
/* 有 LINE 區塊時，信箱框改為次要樣式 */
.bs-code-box--secondary {
  background: #fafafa;
  border-color: #e8ecf0;
  padding: 12px 16px;
  margin-bottom: 16px;
}
.bs-code-box--secondary .bs-code-box__label { font-size: 0.75rem; }
.bs-code-box--secondary .bs-code-box__email { font-size: 0.95rem; }

/* 提醒 */
.bs-reminders {
  text-align: left;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0 0 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bs-reminders li {
  font-size: 0.88rem;
  color: #777;
  padding-left: 18px;
  position: relative;
  line-height: 1.5;
}
.bs-reminders li::before {
  content: '•';
  position: absolute;
  left: 6px;
  color: #8b6f5c;
}
.bs-reminders strong {
  color: #555;
}

/* 按鈕組 */
.bs-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.bs-btn {
  display: block;
  width: 100%;
  padding: 14px 24px;
  border-radius: 8px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  box-sizing: border-box;
  transition: background 0.15s;
}
.bs-btn--primary {
  background: #8b6f5c;
  color: #fff;
}
.bs-btn--primary:hover { background: #7a5f4d; }
.bs-btn--secondary {
  background: transparent;
  border: 1px solid #8b6f5c;
  color: #8b6f5c;
}
.bs-btn--secondary:hover { background: #f5ede8; }
.bs-btn--ghost {
  background: transparent;
  border: 1px solid #ddd;
  color: #aaa;
}
.bs-btn--ghost:hover { background: #f5f5f5; color: #888; }
.bs-btn--line {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  border-radius: 8px;
  background: #06c755;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  box-sizing: border-box;
  margin-bottom: 16px;
  transition: background 0.15s;
}
.bs-btn--line:hover { background: #05b34c; }

/* 預填訊息複製區 */
.bs-line-msg-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 10px;
}
.bs-line-msg-box__text {
  flex: 1;
  font-size: 0.88rem;
  color: #444;
  margin: 0;
  line-height: 1.5;
}

/* LINE 傳訊息提示框 */
.bs-line-notice {
  width: 100%;
  box-sizing: border-box;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 12px;
  text-align: left;
}
.bs-line-notice__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 4px;
}
.bs-line-notice__desc {
  font-size: 0.87rem;
  color: #555;
  margin: 0;
  line-height: 1.6;
}

/* 店家無 LINE 時的提示 */
.bs-no-line-hint {
  font-size: 0.9rem;
  color: #777;
  line-height: 1.6;
  background: #f9f6f3;
  border: 1px solid #e8ddd7;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  width: 100%;
  box-sizing: border-box;
}

@media (min-width: 480px) {
  .bs-actions {
    flex-direction: row;
    justify-content: center;
  }
  .bs-btn {
    width: auto;
    min-width: 160px;
  }
}

.bs-line-btns {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
.bs-btn--copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 13px 24px;
  border-radius: 8px;
  border: 2px solid #06c755;
  background: #fff;
  color: #06c755;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.bs-btn--copy:hover { background: #f0faf4; }

.bs-status-legend {
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: #f0faf4;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #444;
  line-height: 1.8;
  margin-bottom: 16px;
  box-sizing: border-box;
}
.bs-status-legend__title { font-weight: 600; color: #333; }
.bs-status-legend p { margin: 0; }
</style>
